import React, { useState, useEffect } from 'react'
import { InputGroup, H5, Spinner, Classes } from '@blueprintjs/core'
import {
  IconButton,
  Confirmation,
  Flex,
  Button,
  Toaster,
  SpaceCard,
  SpaceCardSkeleton,
  SpaceSelect,
  IGridTableColumn,
  NewGridTable,
  GridTableStatusEnum,
} from '@ui'
import api, { IAssetDetailsSpace } from '@api'
import { ApolloError } from 'apollo-boost'
import { pluralize } from '@utils'
import { useSelection } from '@hooks'
import useAssetSpaces from './hooks/useAssetSpaces'
import track from '@track'
import Event from '@track/Event'
import { Permissions, PermissionsType, IPermissionsOnAsset } from '@permissions'
import classnames from 'classnames'
import TableWrapper from './TableWrapper'

const placeholder = <span className={Classes.SKELETON}>placeholder</span>

export default function AssetSpaces({
  id,
  reloadAssets,
  reloadAsset,
}: {
  id: string
  reloadAssets?: () => void
  reloadAsset?: () => void
}) {
  const selection = useSelection()
  const spaces = useAssetSpaces(id)
  const [showSearch, setShowSearch] = useState(false)
  const [withUndo, setWithUndo] = useState<IAssetDetailsSpace[]>([])
  const [disableConfirmation, setDisableConfirmation] = useState(false)

  useEffect(() => {
    setWithUndo(spaces.list)
  }, [spaces.list])

  const helperText = selection.count
    ? `${selection.count} selected`
    : spaces.query
    ? `${spaces.count} filtered ${pluralize(spaces.count, 'record')}`
    : `${spaces.count} ${pluralize(spaces.count, 'record')}`

  const reload = async () => {
    await reloadAssets?.()
    await reloadAsset?.()
    await spaces.reload()
  }

  const columnsSpaces: IGridTableColumn<IAssetDetailsSpace>[] = [
    {
      label: 'Space',
      Cell: ({ cell }) => {
        return cell ? <SpaceCard space={cell.data} /> : <SpaceCardSkeleton />
      },
    },
    {
      label: 'Department Allocation',
      Cell: ({ cell, skeleton }) => {
        return cell ? (
          <div>
            {cell.data.spaceAssignment?.department
              ? `${cell.data.spaceAssignment.department.name}${
                  cell.data.spaceAssignment?.team ? ' | ' + cell.data.spaceAssignment.team.name : ''
                }`
              : '-'}
          </div>
        ) : (
          <div
            className={classnames({
              [Classes.SKELETON]: skeleton,
            })}
          >
            place
          </div>
        )
      },
    },
    {
      label: '',
      Cell: ({ cell, isHighlighted }) => {
        if (!cell || !isHighlighted) return null

        const { data: space } = cell

        return (
          <Permissions type={PermissionsType.ASSET}>
            {(permissions) => {
              const { canRemoveSpaces } = permissions as IPermissionsOnAsset
              return canRemoveSpaces ? (
                <div className="w-full" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <IconButton
                    round
                    minimal
                    className="remove-icon"
                    iconName="times"
                    tooltip={`Remove ${space.externalId}`}
                    style={{ border: 'none' }}
                    onClick={() => {
                      const removedSpace = space

                      setWithUndo((prev) => [...prev.filter((i) => space.id !== i.id)])
                      selection.clear()

                      track.assetDialog({
                        eventName: Event.RECORD_REMOVE,
                        props: {
                          component: 'Space Manage',
                        },
                      })
                      api.asset
                        .update(id, undefined, undefined, {
                          disconnect: [{ id: space.id }],
                        })
                        .then(() => {
                          reload()
                          Toaster.success({
                            action: {
                              onClick: () => {
                                api.asset
                                  .update(id, undefined, undefined, {
                                    connect: [{ id: space.id }],
                                  })
                                  .then(reload)
                                  .catch((error: ApolloError) => Toaster.fail({ error }))
                                setWithUndo((prev) => [removedSpace, ...prev])

                                track.assetDialog({
                                  eventName: Event.UNDO_CLICK,
                                  props: {
                                    component: 'Space Manage',
                                    actionName: 'remove',
                                  },
                                })
                              },
                              text: 'Undo',
                            },
                            message: 'Asset removed from Space',
                          })
                        })
                        .catch((error: ApolloError) => Toaster.fail({ error }))
                    }}
                  />
                </div>
              ) : null
            }}
          </Permissions>
        )
      },
    },
  ]

  const formatItems = (items: IAssetDetailsSpace[]) => {
    return items.map((i) => ({
      data: i,
    }))
  }

  const formatedItems = formatItems(spaces.list)

  return (
    <>
      <Flex
        align="center"
        justify="space-between"
        style={{
          marginTop: 40,
          height: 40,
        }}
      >
        <Flex align="center">
          <H5 style={{ marginBottom: 0, marginRight: 6, lineHeight: '30px' }}>Spaces</H5>
          {showSearch ? (
            <InputGroup
              autoFocus
              leftIcon="search"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                spaces.run(e.target.value)
              }}
              placeholder="Search spaces..."
              rightElement={
                <RightElement
                  showWithoutQuery
                  query={spaces.query}
                  onClear={() => {
                    if (spaces.query) spaces.run('')
                    setShowSearch(false)
                  }}
                  isQueryResultLoading={spaces.isLoading}
                />
              }
              value={spaces.query}
            />
          ) : (
            <IconButton round minimal iconName="search" onClick={() => setShowSearch(true)} />
          )}
        </Flex>
      </Flex>
      <Flex justify="space-between">
        <Flex align="center">
          {Boolean(selection.count) && (
            <span style={{ paddingRight: 10 }}>
              <Confirmation
                shouldHide={disableConfirmation}
                enabled={!disableConfirmation}
                text={`Remove asset from selected ${pluralize(selection.count, 'Space')}?`}
                onConfirm={() => {
                  const spacesForDelete = withUndo.filter((i) =>
                    Object.keys(selection.items).includes(i.id)
                  )

                  const spacesIds = Object.entries(selection.items)
                    .filter(([_, value]) => Boolean(value))
                    .map(([id]) => id)

                  setWithUndo((prev) => [...prev.filter((i) => !spacesIds.includes(i.id))])

                  track.assetDialog({
                    eventName: Event.BATCH_ACTION,
                    props: {
                      actionName: 'Delete spaces',
                      actionValue: null,
                      batchCount: spacesIds.length,
                    },
                  })

                  api.asset
                    .update(id, undefined, undefined, {
                      disconnect: spacesIds.map((id) => ({ id })),
                    })
                    .then(() =>
                      setWithUndo((prev) => [...prev.filter((i) => !spacesIds.includes(i.id))])
                    )
                    .catch((error: ApolloError) => Toaster.fail({ error }))
                    .then(() => {
                      reload()
                      selection.clear()
                      Toaster.success({
                        action: {
                          onClick: () => {
                            setWithUndo((prev) => [...spacesForDelete, ...prev])
                            setDisableConfirmation(false)
                            api.asset
                              .update(id, undefined, undefined, {
                                connect: spacesIds.map((id) => ({ id })),
                              })
                              .then(reload)
                              .catch((error: ApolloError) => Toaster.fail({ error }))
                            track.assetDialog({
                              eventName: Event.UNDO_CLICK,
                              props: {
                                component: 'Space Manage',
                                actionName: 'remove',
                              },
                            })
                          },
                          text: 'Undo',
                        },
                        message: `Asset removed from ${pluralize(selection.count, 'Space')} `,
                      })
                    })
                }}
              >
                <Button text="Remove" />
              </Confirmation>
            </span>
          )}
          <Flex align="baseline">
            {!spaces.isLoading && <span style={{ color: 'var(--grey1)' }}>{helperText}</span>}
            {spaces.isLoading && placeholder}
            {Boolean(selection.count) && (
              <IconButton
                minimal
                tooltip="Clear selection"
                iconName="times"
                style={{ marginLeft: 4 }}
                data-button="clear_selection"
                onClick={() => {
                  selection.clear()

                  track.assetDialog({
                    eventName: Event.CLEAR_SELECTION,
                    props: {
                      component: 'Space Manage',
                    },
                  })
                }}
              />
            )}
          </Flex>
        </Flex>
        <Permissions type={PermissionsType.ASSET}>
          {(permissions) => {
            const { canAddSpaces } = permissions as IPermissionsOnAsset
            return canAddSpaces ? (
              <SpaceSelect
                placeholder="Space"
                buttonProps={{ text: 'Space', iconName: 'plus' }}
                value={null}
                onChange={(space) => {
                  space &&
                    api.asset
                      .update(id, undefined, undefined, {
                        connect: [{ id: space.id }],
                      })
                      .then(() => {
                        Toaster.success({ message: 'Asset added to Space' })
                        reload()
                      })
                      .catch((error: ApolloError) => Toaster.fail({ error }))

                  track.assetDialog({
                    eventName: Event.RECORD_ADD,
                    props: {
                      component: 'Space Manage',
                    },
                  })
                }}
              />
            ) : (
              <div />
            )
          }}
        </Permissions>
      </Flex>

      <Permissions type={PermissionsType.ASSET}>
        {(permissions) => {
          const { canRemoveSpaces } = permissions as IPermissionsOnAsset
          return (
            // if no ability to remove spaces, we're removing selection
            // and making the 1st column wider
            <TableWrapper withSelection={canRemoveSpaces}>
              <NewGridTable<IAssetDetailsSpace>
                status={spaces.isLoading ? GridTableStatusEnum.LOADING : GridTableStatusEnum.IDLE}
                sticky={false}
                noDataMessage={spaces.query ? undefined : 'No Spaces associated yet'}
                columns={columnsSpaces}
                selection={canRemoveSpaces ? selection : undefined}
                data={formatedItems}
                skeletonSize={3}
              />
            </TableWrapper>
          )
        }}
      </Permissions>

      {spaces.list && spaces.pageInfo.hasNextPage && (
        <Button
          minimal
          style={{ marginTop: 5 }}
          text={`View ${
            spaces.count - spaces.list.length > 5 ? '5' : spaces.count - spaces.list.length
          } more`}
          onClick={() => {
            spaces.loadMore()

            track.assetDialog({
              eventName: Event.PAGE,
              props: {
                component: 'Space Manage',
              },
            })
          }}
        />
      )}
    </>
  )
}

function RightElement({
  showWithoutQuery,
  query,
  onClear,
  isQueryResultLoading,
}: {
  query: string
  onClear: () => void
  isQueryResultLoading: boolean
  showWithoutQuery?: boolean
}) {
  if (isQueryResultLoading && query) return <Spinner size={18} />
  if ((showWithoutQuery !== undefined && showWithoutQuery) || query)
    return (
      <IconButton
        minimal
        iconName="times"
        onClick={() => {
          onClear()
          track.assetDialog({
            eventName: Event.CLEAR_FILTER,
            props: {
              filterName: 'Query',
              filterValue: null,
              component: 'Space Manage',
            },
          })
        }}
      />
    )
  return null
}
